import React, { useMemo } from 'react';
import { Play, Pause, Square, RotateCcw, Clock, AlertCircle } from 'lucide-react';
import Button from '../../../../../components/ui/Button';
import { pluralize } from '../../../../../utils/formatting';

/**
 * Компонент отображения таймера обратного отсчёта
 */
const TimerDisplay = ({ 
  timeRemaining,
  formData,
  isRunning,
  isPaused,
  isCompleted,
  startTimer,
  pauseTimer,
  stopTimer,
  resetTimer
}) => {
  // Проверяем, есть ли данные для отображения
  const hasTimeData = timeRemaining && 
    (timeRemaining.years > 0 || 
     timeRemaining.months > 0 || 
     timeRemaining.days > 0 || 
     timeRemaining.hours > 0 || 
     timeRemaining.minutes > 0 || 
     timeRemaining.seconds > 0);

  // Преобразуем настройки темы в стили
  const themeStyles = useMemo(() => {
    const theme = formData.theme;
    let bgClass = 'bg-white dark:bg-gray-800';
    let textClass = 'text-gray-900 dark:text-white';
    let accentClass = 'text-indigo-600 dark:text-indigo-400';
    let borderClass = 'border-gray-200 dark:border-gray-700';
    
    switch (theme) {
      case 'dark':
        bgClass = 'bg-gray-900';
        textClass = 'text-white';
        accentClass = 'text-indigo-400';
        borderClass = 'border-gray-700';
        break;
      case 'light':
        bgClass = 'bg-gray-50';
        textClass = 'text-gray-900';
        accentClass = 'text-indigo-600';
        borderClass = 'border-gray-200';
        break;
      case 'festive':
        bgClass = 'bg-gradient-to-r from-pink-500 to-purple-500';
        textClass = 'text-white';
        accentClass = 'text-white';
        borderClass = 'border-pink-300';
        break;
      case 'minimalist':
        bgClass = 'bg-white dark:bg-gray-900';
        textClass = 'text-gray-600 dark:text-gray-400';
        accentClass = 'text-gray-900 dark:text-gray-100';
        borderClass = 'border-gray-100 dark:border-gray-800';
        break;
      case 'business':
        bgClass = 'bg-gray-800';
        textClass = 'text-gray-200';
        accentClass = 'text-blue-400';
        borderClass = 'border-gray-700';
        break;
      case 'russian-flag':
        bgClass = 'bg-gradient-to-b from-white via-blue-500 to-red-500';
        textClass = 'text-gray-900';
        accentClass = 'text-white';
        borderClass = 'border-blue-500';
        break;
      case 'victory-day':
        bgClass = 'bg-gradient-to-r from-orange-500 to-black';
        textClass = 'text-white';
        accentClass = 'text-orange-300';
        borderClass = 'border-orange-600';
        break;
      default:
        // default 'standard' theme
        break;
    }
    
    // Если пользователь выбрал свои цвета
    if (formData.backgroundColor && formData.backgroundColor !== '#ffffff') {
      bgClass = `bg-[${formData.backgroundColor}]`;
    }
    
    if (formData.textColor && formData.textColor !== '#000000') {
      textClass = `text-[${formData.textColor}]`;
    }
    
    return { bgClass, textClass, accentClass, borderClass };
  }, [formData.theme, formData.backgroundColor, formData.textColor]);

  // Функция форматирования времени с учётом настроек отображения
  const formatTimeDisplay = () => {
    if (!hasTimeData) return null;
    
    const timeBlocks = [];
    
    // Годы
    if (formData.showYears && timeRemaining.years > 0) {
      timeBlocks.push(
        <div key="years" className="flex flex-col items-center mx-2 min-w-[70px]">
          <span className={`text-5xl font-bold ${themeStyles.accentClass}`}>{timeRemaining.years}</span>
          <span className={`text-sm mt-1 ${themeStyles.textClass}`}>
            {pluralize(timeRemaining.years, 'год', 'года', 'лет')}
          </span>
        </div>
      );
    }
    
    // Месяцы
    if (formData.showMonths && (timeRemaining.months > 0 || timeRemaining.years > 0)) {
      timeBlocks.push(
        <div key="months" className="flex flex-col items-center mx-2 min-w-[70px]">
          <span className={`text-5xl font-bold ${themeStyles.accentClass}`}>{timeRemaining.months}</span>
          <span className={`text-sm mt-1 ${themeStyles.textClass}`}>
            {pluralize(timeRemaining.months, 'месяц', 'месяца', 'месяцев')}
          </span>
        </div>
      );
    }
    
    // Дни
    if (formData.showDays && (timeRemaining.days > 0 || timeRemaining.months > 0 || timeRemaining.years > 0)) {
      timeBlocks.push(
        <div key="days" className="flex flex-col items-center mx-2 min-w-[70px]">
          <span className={`text-5xl font-bold ${themeStyles.accentClass}`}>{timeRemaining.days}</span>
          <span className={`text-sm mt-1 ${themeStyles.textClass}`}>
            {pluralize(timeRemaining.days, 'день', 'дня', 'дней')}
          </span>
        </div>
      );
    }
    
    // Часы
    if (formData.showHours) {
      timeBlocks.push(
        <div key="hours" className="flex flex-col items-center mx-2 min-w-[70px]">
          <span className={`text-5xl font-bold ${themeStyles.accentClass}`}>
            {String(timeRemaining.hours).padStart(2, '0')}
          </span>
          <span className={`text-sm mt-1 ${themeStyles.textClass}`}>
            {pluralize(timeRemaining.hours, 'час', 'часа', 'часов')}
          </span>
        </div>
      );
    }
    
    // Минуты
    if (formData.showMinutes) {
      timeBlocks.push(
        <div key="minutes" className="flex flex-col items-center mx-2 min-w-[70px]">
          <span className={`text-5xl font-bold ${themeStyles.accentClass}`}>
            {String(timeRemaining.minutes).padStart(2, '0')}
          </span>
          <span className={`text-sm mt-1 ${themeStyles.textClass}`}>
            {pluralize(timeRemaining.minutes, 'минута', 'минуты', 'минут')}
          </span>
        </div>
      );
    }
    
    // Секунды
    if (formData.showSeconds && formData.includeSeconds) {
      timeBlocks.push(
        <div key="seconds" className="flex flex-col items-center mx-2 min-w-[70px]">
          <span className={`text-5xl font-bold ${themeStyles.accentClass}`}>
            {String(timeRemaining.seconds).padStart(2, '0')}
          </span>
          <span className={`text-sm mt-1 ${themeStyles.textClass}`}>
            {pluralize(timeRemaining.seconds, 'секунда', 'секунды', 'секунд')}
          </span>
        </div>
      );
    }
    
    // Добавляем двоеточия между блоками времени для классического отображения
    const finalDisplay = [];
    timeBlocks.forEach((block, index) => {
      finalDisplay.push(block);
      if (index < timeBlocks.length - 1) {
        finalDisplay.push(
          <div key={`separator-${index}`} className={`text-4xl font-bold self-center mb-5 ${themeStyles.textClass} opacity-50`}>:</div>
        );
      }
    });
    
    return finalDisplay;
  };

  // Вычисляем отображаемое название таймера
  const getTimerTitle = () => {
    if (formData.timerName) {
      return formData.timerName;
    }
    
    if (formData.timerMode === 'date') {
      const targetDate = new Date(`${formData.targetDate}T${formData.targetTime || '00:00'}:00`);
      return `${formData.timerType === 'countdown' ? 'До' : 'От'} ${targetDate.toLocaleDateString('ru-RU', { 
        day: 'numeric', 
        month: 'long', 
        year: 'numeric' 
      })}`;
    } else {
      return `${formData.timerHours}ч ${formData.timerMinutes}м ${formData.timerSeconds}с`;
    }
  };

  // Если не запущен и нет данных для отображения
  if (!isRunning && !hasTimeData && !isCompleted) {
    return (
      <div className={`mb-8 rounded-xl shadow-md border ${themeStyles.borderClass} ${themeStyles.bgClass} p-8 text-center flex flex-col items-center`}>
        <Clock className={`w-16 h-16 mb-4 ${themeStyles.accentClass}`} />
        <h2 className={`text-2xl font-semibold mb-6 ${themeStyles.textClass}`}>
          Настройте и запустите таймер
        </h2>
        <p className={`text-gray-600 dark:text-gray-400 mb-8`}>
          Установите параметры таймера в форме ниже и нажмите кнопку "Запустить таймер"
        </p>
        <Button 
          primary 
          size="lg"
          icon={<Play className="w-5 h-5" />}
          onClick={startTimer}
        >
          Запустить таймер
        </Button>
      </div>
    );
  }

  // Если таймер завершён
  if (isCompleted) {
    return (
      <div className={`mb-8 rounded-xl shadow-md border ${themeStyles.borderClass} ${themeStyles.bgClass} p-8 text-center`}>
        <AlertCircle className={`w-16 h-16 mx-auto mb-4 ${themeStyles.accentClass}`} />
        <h2 className={`text-2xl font-semibold mb-2 ${themeStyles.textClass}`}>
          {getTimerTitle()}
        </h2>
        <p className={`text-xl font-bold mb-6 ${themeStyles.accentClass}`}>
          Отсчёт завершён!
        </p>
        <div className="flex justify-center gap-3 mt-6">
          <Button 
            variant="secondary" 
            size="md"
            icon={<RotateCcw className="w-4 h-4" />}
            onClick={resetTimer}
          >
            Сбросить
          </Button>
          <Button 
            primary 
            size="md"
            icon={<Play className="w-4 h-4" />}
            onClick={startTimer}
          >
            Начать заново
          </Button>
        </div>
      </div>
    );
  }

  // Основное отображение таймера
  return (
    <div className={`mb-8 rounded-xl shadow-md border ${themeStyles.borderClass} ${themeStyles.bgClass} p-8`}>
      <h2 className={`text-xl font-semibold mb-4 text-center ${themeStyles.textClass}`}>
        {getTimerTitle()}
      </h2>
      
      <div className="flex flex-wrap justify-center items-center py-6">
        {formatTimeDisplay()}
      </div>
      
      <div className="flex justify-center gap-3 mt-6">
        {!isRunning ? (
          <Button 
            primary 
            size="md"
            icon={<Play className="w-4 h-4" />}
            onClick={startTimer}
          >
            {isPaused ? 'Продолжить' : 'Запустить'}
          </Button>
        ) : (
          <Button 
            variant="secondary" 
            size="md"
            icon={<Pause className="w-4 h-4" />}
            onClick={pauseTimer}
          >
            Пауза
          </Button>
        )}
        
        <Button 
          variant={isRunning ? 'danger' : 'secondary'} 
          size="md"
          icon={<Square className="w-4 h-4" />}
          onClick={stopTimer}
          disabled={!isRunning && !isPaused}
        >
          Остановить
        </Button>
        
        <Button 
          variant="secondary" 
          size="md"
          icon={<RotateCcw className="w-4 h-4" />}
          onClick={resetTimer}
          disabled={!isRunning && !isPaused && !hasTimeData}
        >
          Сбросить
        </Button>
      </div>
    </div>
  );
}

export default TimerDisplay;